import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { adminBrandsAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const BrandsManagement = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    image_path: '',
    description: '',
    is_active: true,
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  useEffect(() => {
    loadBrands();
  }, [searchTerm]);

  const loadBrands = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 15,
        ...(searchTerm && { search: searchTerm }),
      };
      const response = await adminBrandsAPI.getAll(params);
      setBrands(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar las marcas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (brand = null) => {
    if (brand) {
      setSelectedBrand(brand);
      setFormData({
        name: brand.name || '',
        image_path: brand.image_path || '',
        description: brand.description || '',
        is_active: brand.is_active ?? true,
      });
    } else {
      setSelectedBrand(null);
      setFormData({
        name: '',
        image_path: '',
        description: '',
        is_active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBrand(null);
    setFormData({
      name: '',
      image_path: '',
      description: '',
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedBrand) {
        await adminBrandsAPI.update(selectedBrand.id, formData);
        toast({
          title: "Éxito",
          description: "Marca actualizada exitosamente",
        });
      } else {
        await adminBrandsAPI.create(formData);
        toast({
          title: "Éxito",
          description: "Marca creada exitosamente",
        });
      }
      handleCloseModal();
      loadBrands();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Error al guardar la marca",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta marca?')) {
      return;
    }
    try {
      await adminBrandsAPI.delete(id);
      toast({
        title: "Éxito",
        description: "Marca eliminada exitosamente",
      });
      loadBrands();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Error al eliminar la marca",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Marcas</h2>
          <p className="text-[#A2A2A2]">Gestiona las marcas del outlet</p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-white text-black hover:bg-white/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva marca
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A2A2A2] w-5 h-5" />
        <Input
          type="text"
          placeholder="Buscar por nombre..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/5 border-white/10 text-white"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#A2A2A2]">Cargando...</div>
      ) : brands.length === 0 ? (
        <div className="text-center py-12 text-[#A2A2A2]">No hay marcas registradas</div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Imagen</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Nombre</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Descripción</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Estatus</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-white">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {brands.map((brand) => (
                <tr key={brand.id} className="hover:bg-white/5">
                  <td className="px-6 py-4">
                    {brand.image_path ? (
                      <img
                        src={brand.image_path}
                        alt={brand.name}
                        className="w-16 h-16 object-cover rounded-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center" style={{ display: brand.image_path ? 'none' : 'flex' }}>
                      <ImageIcon className="w-6 h-6 text-[#A2A2A2]" />
                    </div>
                  </td>
                  <td className="px-6 py-4 text-white font-medium">{brand.name}</td>
                  <td className="px-6 py-4 text-[#A2A2A2] text-sm max-w-md truncate">
                    {brand.description || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      brand.is_active
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {brand.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenModal(brand)}
                        className="text-white hover:bg-white/10"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(brand.id)}
                        className="text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-[#A2A2A2] text-sm">
            Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de {pagination.total}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => loadBrands(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              onClick={() => loadBrands(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-black border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {selectedBrand ? 'Editar marca' : 'Nueva marca'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="image_path">URL de imagen</Label>
              <Input
                id="image_path"
                value={formData.image_path}
                onChange={(e) => setFormData({ ...formData, image_path: e.target.value })}
                placeholder="https://ejemplo.com/imagen.jpg"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label htmlFor="description">Descripción</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-[#A2A2A2] focus:outline-none focus:ring-2 focus:ring-white/20"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Activa</Label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-white text-black hover:bg-white/90">
                {selectedBrand ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandsManagement;

